import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useLocationContext, useProfileContext } from "context";
import { getGooglePosts } from "api/bReviewsApi";

const isMissingMapping = (error) => error?.response?.status === 404;

export default function useHomeScheduledPosts() {
  const { userProfile } = useProfileContext();
  const { locationsData = [], isLocationsLoading } = useLocationContext();
  const organisationId = userProfile?.organisation_id;

  const queries = useQueries({
    queries: locationsData.map((location) => ({
      queryKey: ["google-posts", organisationId, location.id],
      queryFn: () => getGooglePosts(organisationId, location.id),
      enabled: Boolean(organisationId && location.id),
      staleTime: 60_000,
      refetchInterval: 60_000,
      retry: (failureCount, error) => !isMissingMapping(error) && failureCount < 1,
    })),
  });

  const posts = useMemo(() => {
    const now = Date.now();
    return queries.flatMap((query, index) => {
      if (!query.data || query.isError) return [];
      const location = locationsData[index];
      return (query.data.data?.queue || [])
        .filter((item) => (
          ["queued", "scheduled", "processing"].includes(item.status)
          && item.scheduled_for
          && new Date(item.scheduled_for).getTime() > now
        ))
        .map((item) => ({ ...item, locationId: location.id, locationName: location.name || "Location" }));
    }).sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for));
  }, [locationsData, queries]);

  const actionableErrors = queries.filter((query) => query.isError && !isMissingMapping(query.error));
  return {
    posts,
    isLoading: Boolean(organisationId) && (isLocationsLoading || queries.some((query) => query.isPending)),
    isError: actionableErrors.length > 0,
    refetch: () => actionableErrors.length
      ? Promise.all(actionableErrors.map((query) => query.refetch()))
      : Promise.all(queries.map((query) => query.refetch())),
  };
}
